"use client";
// Imports
import { useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  Container,
  Card,
  FilterBar,
  Pagination,
  ComponentWrapper,
  Img,
} from "@/components";
import { refineResults } from "@/lib/helpers";
import useStore from "@/lib/state";
import { useKindeSession } from "@/components/KindeWrapper";
import Link from "next/link";
import { resolveLink } from "@/lib/utils";

// Showbags Grid s
export function CardGrid({
  type,
  heading,
  showFilter,
  showPagination,
  gridData,
  collectionHandle,
  categories,
  competitionTags = [],
  show_ads,
  ads,
  mapIsActive,
}) {
  const { user } = useKindeSession();
  const userId = user?.id || null;

  const adIndexes = show_ads
    ? [6, 12, 18, 24, 30, 36, 42, 48, 54, 60, 66, 72, 78]
    : [];

  // Variables
  const searchParams = useSearchParams();
  const {
    // sortBy,
    // searchTerm,
    // activeCategories,
    setPageBy,
    currentPage,
    pageBy,
    setCurrentPage,
  } = useStore();

  useEffect(() => {
    if (show_ads) {
      setPageBy(96 - adIndexes.length);
    }
  }, []);

  const [sortBy, setSortBy] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeCategories, setActiveCategories] = useState([]);
  const [activeCompetitionTags, setActiveCompetitionTags] = useState([]);
  const [lastGridItemIsAd, setLastGridItemIsAd] = useState(false);

  // Set State from URL Params
  // useEffect(() => {
  //   setSortBy(searchParams.get("sortBy") ? searchParams.get("sortBy") : "");
  //   setSearchTerm(
  //     searchParams.get("searchTerm") ? searchParams.get("searchTerm") : ""
  //   );
  //   setActiveCategories(
  //     searchParams.get("activeCategories")?.split(",")
  //       ? searchParams.get("activeCategories")?.split(",")
  //       : []
  //   );
  //   setCurrentPage(
  //     parseInt(searchParams.get("currentPage"))
  //       ? parseInt(searchParams.get("currentPage"))
  //       : 1
  //   );
  //   if (showPagination) {
  //     if (parseInt(searchParams.get("pageBy")) > 100) {
  //       setPageBy(24);
  //     } else {
  //       setPageBy(searchParams.get("pageBy") ? searchParams.get("pageBy") : 24);
  //     }
  //   } else {
  //     setPageBy(gridData.length);
  //   }
  // }, [searchParams]);

  // Refine Results
  const refinedResults = refineResults(
    gridData,
    sortBy,
    searchTerm,
    activeCategories,
    pageBy,
    currentPage,
    collectionHandle,
    undefined,
    undefined,
    activeCompetitionTags
  );
  const totalResults = refinedResults?.flat().length;
  const startAmount = (currentPage - 1) * pageBy + 1;
  const endAmount = Math.min(
    currentPage * pageBy,
    refinedResults?.flat().length
  );

  // useEffect to push back to page 1 if current page exceeds the new total amount of <results></results>
  useEffect(() => {
    const totalResults = refinedResults?.flat().length;
    const totalPages = Math.ceil(totalResults / pageBy);
    if (currentPage > totalPages && totalResults > 0) {
      setCurrentPage(1);
    }
  }, [refinedResults, currentPage, pageBy, setCurrentPage]);

  const getRandomAd = () => {
    const randomIndex = Math.floor(Math.random() * ads.length);
    return ads[randomIndex];
  };

  // Function to inject ads into the refinedResults array
  const injectAdsIntoPage = (pageResults, adIndexes) => {
    const resultsWithAds = [...pageResults];
    adIndexes.forEach((index, i) => {
      const ad = getRandomAd();
      resultsWithAds.splice(index + i, 0, { isAd: true, content: ad });
    });
    return resultsWithAds;
  };

  // Inject ads into each page's results array
  const paginatedResultsWithAds = refinedResults.map((pageResults) =>
    injectAdsIntoPage(pageResults, adIndexes)
  );

  // Red gum accents instead of theme saddle orange
  const redGumThemeStyle =
    collectionHandle === "rides" ||
    collectionHandle === "commercial_exhibitors" ||
    collectionHandle === "showbags" ||
    collectionHandle === "food_outlets"
      ? {
          "--button-background": "137 33 2",
          "--button-hover-background": "137 33 2",
          "--button-foreground": "255 255 255",
        }
      : undefined;

  // Render
  return (
    <ComponentWrapper type={type}>
      <Container>
        <div className="w-full" style={redGumThemeStyle}>
        {showFilter && (
          <FilterBar
            collectionHandle={collectionHandle}
            results={{ startAmount, endAmount, totalResults }}
            categories={categories}
            searchParams={searchParams}
            setSearchTerm={setSearchTerm}
            setSortBy={setSortBy}
            setActiveCategories={setActiveCategories}
            activeCategories={activeCategories}
            tagItems={competitionTags}
            setActiveTags={setActiveCompetitionTags}
            activeTags={activeCompetitionTags}
          />
        )}
        {heading && <h3 className="pb-6 level-2">{heading}</h3>}
        <div
          className="card-grid"
          id="grid_container"
          key={[
            collectionHandle,
            currentPage,
            sortBy,
            searchTerm,
            activeCategories.join(","),
            activeCompetitionTags.join(","),
          ].join("|")}
        >
          {paginatedResultsWithAds[currentPage - 1]?.length > 0 ? (
            paginatedResultsWithAds[currentPage - 1]?.map((page, index) => {
              // if index is 5 or 11 or 17, show ads
              if (page.isAd && show_ads) {
                return (
                  <CardAd
                    lastGridItemIsAd={lastGridItemIsAd}
                    setLastGridItemIsAd={setLastGridItemIsAd}
                    index={index}
                    results={paginatedResultsWithAds[currentPage - 1]?.length}
                    ad={page.content}
                    activeCategories={activeCategories}
                    activeCompetitionTags={activeCompetitionTags}
                    key={`ad-${page.content?.id ?? index}`}
                  />
                );
              } else {
                return (
                  <Card
                    mapIsActive={mapIsActive}
                    setLastGridItemIsAd={setLastGridItemIsAd}
                    userId={userId}
                    key={page.id ?? page.slug ?? index}
                    index={index}
                    collectionHandle={collectionHandle}
                    data={page}
                    priority={index < 4}
                  />
                );
              }
            })
          ) : (
            <div className="flex flex-col col-span-4 justify-center items-center p-12 space-y-4 w-full h-full">
              <h1>No Results</h1>
              <p>Try adjusting your search</p>
            </div>
          )}
        </div>
        <div className="pt-10">
          {showPagination && (
            <Pagination
              results={{ startAmount, endAmount, totalResults }}
              searchParams={searchParams}
            />
          )}
        </div>
        </div>
      </Container>
    </ComponentWrapper>
  );
}

function CardAd({
  index,
  results,
  ad,
  activeCategories,
  activeCompetitionTags,
  lastGridItemIsAd,
  setLastGridItemIsAd,
}) {
  const [clientAd, setClientAd] = useState(null);

  useEffect(() => {
    setClientAd(ad);
  }, [ad]);

  useEffect(() => {
    if (
      ad?.rectangle_desktop &&
      results >= index &&
      index === results - 1
    ) {
      setLastGridItemIsAd(true);
    }
  }, [ad, results, index, setLastGridItemIsAd]);

  if (!ad?.rectangle_desktop) {
    return null;
  }

  if (results < index) {
    return null;
  }

  if (lastGridItemIsAd) {
    return null;
  }

  if (
    !clientAd ||
    activeCategories.length > 0 ||
    activeCompetitionTags.length > 0
  ) {
    return null;
  }

  return (
    <div className="flex flex-col justify-center items-center">
      {clientAd.rectangle_desktop && (
        <Link
          href={resolveLink(clientAd.link) ?? "#"}
          data-ad={clientAd?.title}
          target="_blank"
          rel="noopener noreferrer"
          className="relative block w-full aspect-square overflow-hidden"
        >
          <Img
            data={clientAd.rectangle_desktop}
            fill
            sizes="(max-width: 1024px) 50vw, 400px"
            className="object-cover"
          />
        </Link>
      )}
      <p className="pt-3 w-full font-body text-xs text-center text-brand-espresso/70">
        Advertisement
      </p>
    </div>
  );
}
